import React, { useCallback, ChangeEvent, useMemo, useState, useEffect, useRef } from "react";
import { Route, Routes } from "react-router-dom";
import ContactUs from "./ContactUs";
import { Card, Input, Icon, Typeahead, Search } from "@citi-icg-172888/icgds-react";
import FooterButtonHoc from "../common/FooterButtonHoc";
import { IClientInfo, ICompanyInfo } from "../services/types/LeadRequisitionType";
import { useData } from "../services/context/DataContext";
import SelectHoc from "../common/SelectHoc";
import { handleScript } from "../services/utils/utils";
import {
  CitiPartner_Options,
  INIT_STEPS,
  referralSource,
  referralSource_CitiPartner,
} from "../services/constants/InitialConstants";

import CommonHeader from "./CommonHeader";
import { CONTACT_INFO_INIT } from "../services/types/LeadRequisitionType";
import { validateInputField } from "../services/utils/utils";
import _ from "lodash";
import CustomAlert from "./components/CustomAlert";
import { Layout } from "@citi-icg-172888/icgds-patterns-react";

interface DNCALseExternalProps {
  onAcknowledge: () => void;
}

const DNCALse: React.FC<DNCALseExternalProps> = ({ onAcknowledge }) => {
  const {
    staticData,
    isAboutBusiness,
    setIsAboutBusiness,
    companyInfo,
    setCompanyInfo,
    setIsBasicInfo,

    isCardOpen,
    setIsSubmitted,
    setBusinessNeedInfo,
    setContactInfo,
    setSteps,
    setIsCardOpen,
  } = useData();
  const [header, setHeader] = useState("Let's Begin!");
  // const [initialSetup] = useState<any>(INIT_STEPS);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const ref = useRef<any>(null);
  const formRef = useRef<HTMLDivElement>(null); // Ref for the form element
  const headerRef = useRef<HTMLHeadingElement>(null);
  const [searchBusiness, setSearchBusiness] = useState<boolean>(false);
  const [options, setOptions] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [showAlert, setShowAlert] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Partial<Record<string, boolean>>>({});
  const cardRef = useRef<HTMLDivElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const changeIconRef = useRef<any>(null); // Create a ref for the div

  const validateFields = (
    updatedCompanyData: ICompanyInfo | null,
    updatedTouchedFields = touchedFields
  ) => {
    const newErrors: Partial<Record<string, string>> = { ...errors };

    if (
      updatedTouchedFields.referral &&
      !validateInputField(updatedCompanyData?.businessInfo?.referral)
    ) {
      newErrors.referral = "Please select referral";
    }

    if (
      updatedTouchedFields.referralDetails &&
      updatedCompanyData?.businessInfo?.referral &&
      referralSource_CitiPartner.includes(updatedCompanyData?.businessInfo?.referral)
    ) {
      if (!validateInputField(updatedCompanyData?.businessInfo?.referralDetails))
        newErrors.referralDetails = "Please enter the Partner name before continuing";
    }

    setErrors(newErrors);
    return _.isEmpty(newErrors);
  };

  useEffect(() => {
    if (searchBusiness && changeIconRef.current) {
      changeIconRef.current.focus();
    }
  }, [searchBusiness]);

  useEffect(() => {
    if (window.innerWidth < 768 && window.parent.location.href.includes("contact-us")) {
      window.parent.scroll(0, 0);
    } else if (window.parent.location.href.includes("contact-us")) {
      window.parent.scroll({ top: 0, behavior: "smooth" });
    }
  }, [isAboutBusiness]);

  useEffect(() => {
    if (ref.current && !_.isEmpty(errors)) {
      ref.current?.focus();
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [!errors]);

  useEffect(() => {
    const getCitiPartnerData = async () => {
      try {
        if (searchText.length > 2) {
          const allOptions = staticData?.citiPartner || CitiPartner_Options;
          const filteredOptions = allOptions.filter((option: any) =>
            option.toLowerCase().includes(searchText.toLowerCase())
          );
          setOptions(filteredOptions);
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error("🚀 ~ getCitiPartnerData ~ error:", error);
      }
    };
    getCitiPartnerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const onChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>, ddName: any) => {
      setSearchBusiness(false);
      const updatedClientInfo = {
        ...companyInfo?.businessInfo,
        [ddName]: event.target ? event.target.value : event,
      } as IClientInfo;

      if (ddName === "referral" && referralSource.includes(event as any)) {
        updatedClientInfo["referralDetails"] = "";
      }
      if (ddName === "referralDetails") {
        if (referralSource_CitiPartner.includes(companyInfo?.businessInfo?.referral as any)) {
          setSearchBusiness(true);
        }
      }
      const updateCompanyInfo = { ...companyInfo, businessInfo: updatedClientInfo } as ICompanyInfo;
      setTouchedFields((prev) => {
        const newTouchedFields = { ...prev, [ddName]: true };
        validateFields(updateCompanyInfo, newTouchedFields);
        return newTouchedFields;
      });
      setCompanyInfo(updateCompanyInfo);
    },
    [companyInfo, setCompanyInfo]
  );

  useEffect(() => {
    setHeader("Let's Begin!");
  }, [!companyInfo]);

  useEffect(() => {
    if (companyInfo?.businessInfo?.referralDetails) {
      setSearchBusiness(true);
    }
    if (searchBusiness && cardRef.current) {
      cardRef.current.focus();
    }
  }, [searchBusiness]);

  useEffect(() => {
    const learnMoreLink = document.getElementById("learn_more_link");
    const contactUsBtn = document.getElementById("contact_us");
    if (learnMoreLink) {
      learnMoreLink.setAttribute("tabindex", "-1"); // Prevent focus
    }
    if (contactUsBtn) {
      contactUsBtn.setAttribute("tabindex", "-1"); // Prevent focus
    }
  }, []);

  const enabledSubmit = useMemo(() => {
    return (
      companyInfo?.businessInfo?.referral &&
      (referralSource_CitiPartner.includes(companyInfo?.businessInfo?.referral)
        ? companyInfo?.businessInfo?.referralDetails
        : true)
    );
  }, [companyInfo?.businessInfo]);

  const onSubmitHandler = () => {
    setIsSubmitClicked(true);
    if (enabledSubmit) {
      setIsAboutBusiness(true);
      setIsBasicInfo(false);
      setShowAlert(false);
      // Scroll to the header
      handleScript(divRef, "ccbContactUsFormReferralInfoCompleted");
      if (window.parent.location.href.includes("contact-us"))
        headerRef.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      setShowAlert(true);
      setTouchedFields(() => {
        const newTouchedFields = {
          referral: true,
          referralDetails: true,
        };
        validateFields(companyInfo, newTouchedFields);
        return newTouchedFields;
      });
    }
    if (ref.current) {
      ref.current?.focus();
      if (window.parent.location.href.includes("contact-us"))
        ref.current?.scrollIntoView({ behavior: "smooth" });
    }
    if (window.parent.location.href.includes("contact-us")) window.scrollTo(0, 0);
  };

  const onClickCloseSearchHandler = useCallback(() => {
    setCompanyInfo(null);
    setIsAboutBusiness(false);
    onAcknowledge();
  }, [onAcknowledge]);

  const toggleCardVisibility = () => {
    if (!isCardOpen) {
      setIsSubmitted(false);
      setBusinessNeedInfo(() => ({ businessNeeds: [] }));
      setCompanyInfo(null);
      setContactInfo(CONTACT_INFO_INIT);
      setIsAboutBusiness(false);
      setIsBasicInfo(false);
      setSteps(INIT_STEPS);
      onAcknowledge();
    }
    setIsCardOpen(!isCardOpen);
  };
  const handleAlert = () => {
    setIsSubmitClicked(!isSubmitClicked);
  };

  const onSearchHandler = (event: any) => {
    setSearchText(event);
  };

  const onChangeCompanyData = () => {
    const updatedClientInfo = {
      ...companyInfo?.businessInfo,
    } as IClientInfo;
    updatedClientInfo["referralDetails"] = "";
    const updateCompanyInfo = { ...companyInfo, businessInfo: updatedClientInfo } as ICompanyInfo;
    setSearchBusiness(false);
    setCompanyInfo(updateCompanyInfo);
  };

  const handleTypeaheadChanges = () => {
    if (searchBusiness) {
      return (
        <Card
          className="dnca-lmn-rounded-8 lmn-dnca-w-m-50 lmn-dnca-w-l-50 lmn-dnca-w-s-100 lmn-dnca-w-xs-100"
          id={"typeahead_CitiPartner_card"}
          ref={ref}
          aria-describedby="typeahead_CitiPartner_card_label"
        >
          <div className="lmn-d-flex lmn-align-items-center">
            <div
              className="lmn-flex-grow-1 lmn-text-color-desc"
              id="typeahead_CitiPartner_card_label"
              aria-live="polite" // Ensures the content is announced when it changes
            >
              {companyInfo?.businessInfo?.referralDetails ? (
                <span
                  tabIndex={-1}
                  ref={changeIconRef} // Attach the ref to the div
                  key={companyInfo?.businessInfo?.referralDetails}
                >
                  {companyInfo?.businessInfo?.referralDetails}
                </span>
              ) : (
                "No referral details available"
              )}
            </div>
            <div
              id="citipartner_change_icon"
              style={{
                cursor: "pointer",
              }}
              onClick={() => {
                onChangeCompanyData();
                const inputElement = document.getElementById("typeahead_input");
                if (inputElement) {
                  inputElement.focus();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={
                companyInfo?.businessInfo?.referralDetails
                  ? `Selected  ${companyInfo?.businessInfo?.referralDetails}, Change Partner name`
                  : "Change Partner name"
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  onChangeCompanyData(); // Properly invoke the function
                }
              }}
            >
              <a
                className="lmn-text-link-quiet lmn-text-color-desc lmn-hover-underline lmn-d-md-inline"
                style={{ color: "#fff !important" }}
                target="_blank"
                aria-label="Edit Partner name"
              >
                <Icon
                  type="edit"
                  alt="icon presenting Change Partner name"
                  aria-hidden="true" // Mark as hidden for screen readers
                />
                Change
              </a>
            </div>
          </div>
        </Card>
      );
    } else {
      const isValuePresent = Array.isArray(options) && options.some((option) => option !== "");

      return (
        <div
          id={"typeahead_CitiPartner_search"}
          className="lmn-form-group lmn-mb-5 lmn-dnca-w-m-75 lmn-dnca-w-l-50 lmn-dnca-w-s-100 lmn-dnca-w-xs-100"
        >
          <label
            className="lmn-font-size-xs lmn-card-title-label lmn-required"
            id="partner-name"
            htmlFor="typeahead_input"
          >
            Please provide Partner name
          </label>
          <Typeahead
            id="typeahead_input"
            value={companyInfo?.businessInfo?.referralDetails}
            onSearch={(e: any) => onSearchHandler(e)}
            onChange={(options: any) => onChangeHandler(options, "referralDetails")}
            options={options}
            allowClear={false}
            invalid={!!errors.referralDetails} // Fix: Convert string to boolean
            highlightOption
            popupStyle={{
              display: searchText.length > 2 && isValuePresent ? "" : "none",
            }}
            aria-required={true}
            aria-labelledby="partner-name"
            aria-invalid={!!errors.referralDetails}
            aria-describedby={errors.referralDetails ? "error-message" : undefined}
          >
            <Search
              size="md"
              placeholder="Type here"
              iconSuffix={
                <i
                  role="img"
                  aria-label="search"
                  className="lmn-input-suffix-icon lmnicon lmnicon-search"
                  tabIndex={0} // Make the icon focusable
                  onClick={() => {
                    const inputElement = document.getElementById("typeahead_input");
                    if (inputElement) {
                      inputElement.focus(); // Move focus back to the input field
                    }
                  }}
                />
              }
            />
          </Typeahead>
          {errors.referralDetails && (
            <small id="error-message" role="alert" aria-live="assertive">
              {errors.referralDetails}
            </small>
          )}
        </div>
      );
    }
  };

  return (
    <>
      <Card
        className="ccbDncaWrapper lmn-container content-container lmn-dnca-w-xl-60  lmn-dnca-xl-p-9"
        role="main"
      >
        <div ref={divRef}></div>
        <Card id="no-border" className="ccbDncaWrapper lmn-container lmn-dnca-w-xl-95  no-border">
          <div className="ccbDncaWrapper lmn-container margin-header">
            <CommonHeader toggleCardVisibility={toggleCardVisibility} ref={headerRef} />
          </div>
          {isCardOpen && (
            <div
              ref={formRef}
              className="ccbDncaWrapper lmn-container  lmn-d-flex lmn-flex-column lmn-dnca-w-xl-95 lmn-dnca-w-l-75 app-container"
            >
              <div className="ccbDncaWrapper lmn-container content-container" role="main">
                {!isAboutBusiness && (
                  <>
                    <Card id="contact_us_card">
                      <h3 id="header" className="lmn-text-color-desc lmn-mb-6">
                        {header}
                      </h3>
                      <div className="lmn-dnca-w-l-100 lmn-dnca-w-m-100 lmn-dnca-w-s-100">
                        {isSubmitClicked && showAlert && Object.values(errors).length > 0 && (
                          <CustomAlert
                            id="external-index"
                            errors={Object.values(errors)}
                            handleChange={handleAlert}
                            isSubmitClicked={isSubmitClicked}
                          />
                        )}
                      </div>
                      <Card body className="lmn-mb-3">
                        <div className="lmn-mb-6">
                          <div className="lmn-d-flex lmn-text-color-desc">
                              We’ll guide you through some questions about your business to make sure 
                              the right person contacts you.
                          </div>
                        </div>
                        <Layout type="divider" layoutClass="cust-border-color" />
                        {/* <div className="lmn-dcna-l-flex lmn-mr-2 lmn-dnca-w-100 lmn-row">
                          {initialSetup.map((item: any, index: any) => {
                            return (
                              <div
                                key={index}
                                className="lmn-mb-5  lmn-col-3"
                                style={{ color: "#fff" }}
                              >
                                <Icon
                                  type={item.type}
                                  style={{
                                    fontSize: "30px",
                                    borderRadius: "50px",
                                    color: "#fff",
                                    background: "#255be3",
                                  }}
                                  size="default"
                                  alt={`Icon representing ${item.title}`}
                                  aria-label={`Icon representing ${item.title}`}
                                  aria-hidden="true" // Add this attribute to ignore the icon
                                  className="lmn-ui-lg lmn-ui-md lmn-badge lmn-mx-2 lmn-icon-h-w"
                                />
                                <div className="step-title-margin lmn-mt-4">
                                  <div className="lmn-text-color lmn-dnca-style">
                                    {`Step ${item.step}`} <br /> {item.title}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div> */}

                        <div className="lmn-form-group lmn-mb-5 lmn-dnca-w-m-50 lmn-dnca-w-l-50 lmn-dnca-w-s-100">
                          <label className="lmn-required lmn-card-title-label" htmlFor="referral">
                            How did you hear about us?
                          </label>
                          <SelectHoc
                            id="referral"
                            label="How did you hear about us?"
                            aria-label="How did you hear about us?"
                            value={companyInfo?.businessInfo?.referral || ""}
                            options={staticData?.referralSource || []}
                            error={errors.referral}
                            required={true}
                            onChange={(options: any) => onChangeHandler(options, "referral")}
                            aria-describedby={errors.referral ? "referral-error" : undefined}
                          />
                          {errors.referral && (
                            <small id="referral-error" role="alert" aria-live="assertive">
                              <span className="visually-hidden">How did you hear about us? - </span>
                              {errors.referral}
                            </small>
                          )}
                        </div>
                        {companyInfo?.businessInfo?.referral &&
                          referralSource.includes(companyInfo?.businessInfo?.referral) && (
                            <div className="lmn-form-group lmn-mb-5 lmn-dnca-w-m-75 lmn-dnca-w-l-50 lmn-dnca-w-s-100 lmn-dnca-w-xs-100">
                              <label className="lmn-font-size-xs lmn-card-title-label">
                                Please provide more details (optional)
                              </label>
                              <Input
                                id="referral_more_details"
                                value={companyInfo?.businessInfo?.referralDetails}
                                onChange={(options: any) =>
                                  onChangeHandler(options, "referralDetails")
                                }
                                aria-label={`Please provide more details (optional) 100 max character, you have ${100 - (companyInfo?.businessInfo?.referralDetails?.length ?? 0)} characters left`}
                                maxLength={100}
                                size="md"
                              />
                              <small className="lmn-hint-text-color">
                                100 max character, you have [
                                {100 - (companyInfo?.businessInfo?.referralDetails?.length ?? 0)}]
                                characters left
                              </small>
                            </div>
                          )}
                        {companyInfo?.businessInfo?.referral &&
                          referralSource_CitiPartner.includes(
                            companyInfo?.businessInfo?.referral
                          ) && <>{handleTypeaheadChanges()}</>}
                      </Card>
                      <FooterButtonHoc
                        submitText="Continue"
                        onCancel={onClickCloseSearchHandler}
                        onSubmit={onSubmitHandler}
                      />
                    </Card>
                  </>
                )}
                {isAboutBusiness && (
                  <Routes>
                    <Route path="*" element={<ContactUs />} />
                  </Routes>
                )}
              </div>
            </div>
          )}
        </Card>
      </Card>
    </>
  );
};

export default DNCALse;
